import { usePageStore } from '@/stores/pageStore';
import { useRuntimeConfig } from '#app';

export function useWpPage() {
  const store = usePageStore();
  let lastSameComponentIndex = null;
  const config = useRuntimeConfig();

  async function fetchPageData(identifier: string | number) {
    if (!identifier) {
      throw new Error('Identifier is required for fetching page data.');
    }

    let url = `${config.public.wordpressApiUrl}/wp-json/wp/v2/pages/`;

    if (typeof identifier === 'number') {
      url += identifier; // Fetch by ID
    } else {
      url += `?slug=${identifier}`; // Fetch by slug
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`La page n'a pas pu être chargée (statut: ${response.status})`);
    }
    const data = await response.json();


    let rawPageData = null;
    if (Array.isArray(data)) {
      rawPageData = data.length > 0 ? data[0] : null;
    } else {
      rawPageData = data; // If fetching by ID, it returns a single object
    }

    if (!rawPageData) {
      throw new Error(`La page "${identifier}" n'a pas été trouvée.`, { cause: 'not-found'} );
    }
    let rawHead = rawPageData.yoast_head_json;
    // Set page ID in store (this is a side effect, but useful for other parts of the app)
    store.setPageId(rawPageData.id);

    if (rawPageData.acf && rawPageData.acf.page_content) {
      const apiUrl = config.public.wordpressApiUrl;

      const processedContent = await Promise.all(rawPageData.acf.page_content.map(async (item: any, key: number) => {
      const componentProps: { [key: string]: any } = {};

        // Logic for reverse property based on consecutive same components
        if (item.acf_fc_layout === rawPageData.acf.page_content[key - 1]?.acf_fc_layout) {
          if (lastSameComponentIndex !== key - 1) {
            componentProps.reverse = true;
            lastSameComponentIndex = key;
          } else {
            componentProps.reverse = false;
          }
        } else {
          componentProps.reverse = false;
        }        

        // Map common properties
  
        if (item.title) componentProps.title = item.title;
        if (item.upper_title) componentProps.upperTitle = item.upper_title;
        if (item.content) componentProps.content = item.content;
        if (item.button_label) componentProps.buttonLabel = item.button_label;
        if (item.link) {
          componentProps.buttonLink =  ( item.link.startsWith(apiUrl) &&
            item.acf_fc_layout !== 'Button' )
            ? item.link.replace(apiUrl, '')
            : item.link;            
        }
        if (item.points) componentProps.points = item.points;
        if (item.cards) componentProps.cards = item.cards;
        if (item.after_image_content) componentProps.afterImageContent = item.after_image_content;
        if (item.button) componentProps.button = item.button;
        if (item.image) componentProps.image = item.image.url;

        // Specific handling for VideoTextSection
        if (item.acf_fc_layout === 'VideoTextSection' && item.content) {
          componentProps.content = await Promise.all(item.content.map(async (videoItem: any) => {
            const videoUrl = videoItem.video ? videoItem.video.url : null;
            return {
              title: videoItem.title,
              content: videoItem.content,
              buttonLabel: videoItem.button_content,
              buttonLink: videoItem.link.startsWith(apiUrl)
                ? videoItem.link.replace(apiUrl, '')
                : videoItem.link,
              video: videoUrl,
            };
          }));
        }

        // Specific handling for BookingGenerating
        if (item.acf_fc_layout === 'BookingGenerating' && item.images && Array.isArray(item.images)) {
          componentProps.image = item.images;
        }

        // Specific handling for HeroBanner
        if (item.acf_fc_layout === 'HeroBanner') {
          if (identifier === "primary") { // Assuming "primary" is a slug for a specific homepage hero
            componentProps.heroFirstVideoSrc = item.hero_first_video_src || '/videos/discover-orion.mp4';
            componentProps.heroSecondVideoSrc = item.hero_second_video_src || '/videos/full-video.mp4';
          } else {
            componentProps.backgroundImage = item.background_image.url || '';
            componentProps.backgroundImageDesktop = item.background_image_desktop.url || '';
          }
        }
        
        
        return {
          key,
          component: item.acf_fc_layout,
          props: componentProps,
        };
      }));
      return { content: processedContent, header: rawHead };
    } else if (rawPageData.content && rawPageData.content.rendered) {
      return { content: rawPageData.content.rendered, header: rawHead };
    } else {
      throw new Error(`Aucun contenu ACF ou standard trouvé pour l'identifiant "${identifier}"`);
    }
  }

  return { fetchPageData  };
}

